// KanbanCard — FE-PINV-070.
//
// Single-opportunity card rendered inside a Kanban column. Carries
// more context than Grid Card-mode cards: type badge + lives band +
// monospace renewal anchor + producer avatar + cadence-health edge
// + alert chip + the shared hover action menu.
//
// Drag payload is identical to FE-SCRM-008's Grid OpportunityCard so
// the BE-side `/move-stage` semantics are reused. No renewal-month
// drag here (Kanban has no month axis); the drop target sets
// `renewal_year` / `renewal_month` to the source values so the
// existing handler treats it as a pure stage move.

import { AvatarCircle } from "@/design-system";

import {
  CadenceHealthEdge,
  OpportunityCardActionMenu,
} from "@/features/sales/components";

import type { RenewalBoardOpportunity } from "./types";


const TYPE_BADGE: Record<RenewalBoardOpportunity["opportunity_type"], string> = {
  new_business: "NEW",
  bor_takeover: "BOR",
  rfp_response: "RFP",
  expansion: "EXP",
};


const MONTH_ABBR_UPPER = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];


function renewalLabel(month: number, year: number): string {
  const m = MONTH_ABBR_UPPER[month - 1] ?? "—";
  const yy = (year % 100).toString().padStart(2, "0");
  return `${m} ${yy}`;
}


function livesBandLabel(
  band: RenewalBoardOpportunity["lives_band"],
): string | null {
  switch (band) {
    case "micro": return "Micro";
    case "small": return "Small";
    case "mid_market": return "Mid market";
    case "large": return "Large";
    case "enterprise": return "Enterprise";
    default: return null;
  }
}


export interface KanbanCardProps {
  opp: RenewalBoardOpportunity;
  /** Optional title for the card body line below the prospect name.
   * Falls back to the opportunity type label. */
  opportunity_title?: string | null;
  onClick?: (opportunity_id: string) => void;
}


export function KanbanCard({
  opp, opportunity_title, onClick,
}: KanbanCardProps): JSX.Element {
  const lives = livesBandLabel(opp.lives_band);
  return (
    <div
      data-testid={`kanban-card-${opp.opportunity_id}`}
      data-cadence-health={opp.cadence_health}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", opp.opportunity_id);
        e.dataTransfer.setData(
          "application/x-renewal-board-opp",
          JSON.stringify({
            opportunity_id: opp.opportunity_id,
            stage: opp.stage,
            renewal_year: opp.renewal_year,
            renewal_month: opp.renewal_month,
          }),
        );
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={(e) => {
        // Don't fire the row-click when the producer hits the kebab
        // — the menu trigger calls stopPropagation already, but we
        // belt-and-braces ignore clicks on menus / buttons inside.
        const target = e.target as HTMLElement;
        if (target.closest("[role='menu'], button")) return;
        onClick?.(opp.opportunity_id);
      }}
      className={[
        "relative flex flex-col gap-1.5 rounded-md bg-raised pl-3 pr-2",
        "py-2 text-xs shadow-sm cursor-pointer hover:bg-recessed",
        "group focus-within:ring-2 focus-within:ring-teal",
      ].join(" ")}
    >
      <CadenceHealthEdge state={opp.cadence_health} variant="card" />

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-text-primary truncate">
            {opp.prospect_name}
          </div>
          <div className="text-text-tertiary truncate">
            {opportunity_title
              ?? opp.opportunity_type.replace(/_/g, " ")}
          </div>
        </div>
        <div className="flex items-start gap-1 shrink-0">
          <span
            data-testid="kanban-card-type-badge"
            className={[
              "inline-flex items-center rounded bg-canvas px-1.5",
              "py-0.5 text-[10px] font-semibold tracking-wide",
              "text-text-secondary",
            ].join(" ")}
          >
            {TYPE_BADGE[opp.opportunity_type]}
          </span>
          <div
            className={[
              "opacity-0 group-hover:opacity-100 focus-within:opacity-100",
              "transition-opacity",
            ].join(" ")}
          >
            <OpportunityCardActionMenu
              opportunity_id={opp.opportunity_id}
              prospect_name={opp.prospect_name}
              stage={opp.stage}
              opportunity_type={opp.opportunity_type}
              value_estimate={opp.value_estimate}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-[11px] text-text-tertiary">
        {lives && <span>{lives}</span>}
        {lives && <span>•</span>}
        <span
          data-testid="kanban-card-renewal"
          className="font-mono tracking-wide"
        >
          {renewalLabel(opp.renewal_month, opp.renewal_year)}
        </span>
        {opp.form5500_alert_count > 0 && (
          <>
            <span>•</span>
            <span
              data-testid="kanban-card-alerts"
              className="inline-flex items-center gap-0.5 rounded-full bg-ember/10 px-1.5 py-0.5 text-ember"
              title={`${opp.form5500_alert_count} active alert(s)`}
            >
              ⚑ {opp.form5500_alert_count}
            </span>
          </>
        )}
      </div>

      {opp.producer_id && (
        <div className="absolute bottom-2 right-2">
          <span data-testid="kanban-card-producer-avatar">
            <AvatarCircle
              size="sm"
              initials="?"
              alt={`Producer ${opp.producer_id.slice(0, 4)}`}
            />
          </span>
        </div>
      )}
    </div>
  );
}
